// Reusable UI primitives — Btn, Chip, Card, Row, Avatar, SectionLabel,
// plus IconButton helper. Inline-style approach mirrors the source.

import type { ComponentChildren, JSX } from 'preact';
import { T } from './palette';
import { Icon, type IconName } from './icons';

// ─────────────────────────────────────────────────────────
// Chip
// ─────────────────────────────────────────────────────────

export type ChipTone = 'neutral' | 'brand' | 'safety' | 'danger' | 'info' | 'ink';

export type ChipProps = {
  children?: ComponentChildren;
  tone?: ChipTone;
  icon?: IconName;
  onClick?: JSX.MouseEventHandler<HTMLSpanElement>;
  style?: JSX.CSSProperties;
};

type ToneStyle = { bg: string; fg: string; bd: string };

const CHIP_TONES: Record<ChipTone, ToneStyle> = {
  neutral: { bg: T.surfaceAlt, fg: T.ink2, bd: T.border },
  brand: { bg: T.brandSoft, fg: T.brandDeep, bd: T.brandSoft2 },
  safety: { bg: T.safetySoft, fg: T.safety, bd: '#E8D7A8' },
  danger: { bg: T.dangerSoft, fg: T.danger, bd: '#E9C7BF' },
  info: { bg: T.infoSoft, fg: T.info, bd: '#C8D4E8' },
  ink: { bg: T.ink, fg: '#fff', bd: T.ink },
};

export function Chip({
  tone = 'neutral',
  children,
  icon,
  onClick,
  style,
}: ChipProps): JSX.Element {
  const tones = CHIP_TONES[tone];
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: icon ? '4px 10px 4px 8px' : '4px 10px',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: -0.1,
        color: tones.fg,
        background: tones.bg,
        border: `1px solid ${tones.bd}`,
        borderRadius: 999,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────
// Btn
// ─────────────────────────────────────────────────────────

export type BtnKind = 'primary' | 'ghost' | 'outline' | 'soft' | 'danger' | 'ink';
export type BtnSize = 'sm' | 'md' | 'lg';

export type BtnProps = {
  children?: ComponentChildren;
  kind?: BtnKind;
  size?: BtnSize;
  icon?: IconName;
  iconAfter?: IconName;
  onClick?: JSX.MouseEventHandler<HTMLButtonElement>;
  style?: JSX.CSSProperties;
  disabled?: boolean;
};

const BTN_KINDS: Record<BtnKind, ToneStyle> = {
  primary: { bg: T.brand, fg: '#fff', bd: T.brand },
  ghost: { bg: 'transparent', fg: T.ink, bd: 'transparent' },
  outline: { bg: T.surface, fg: T.ink, bd: T.borderStrong },
  soft: { bg: T.brandSoft, fg: T.brandDeep, bd: T.brandSoft2 },
  danger: { bg: T.danger, fg: '#fff', bd: T.danger },
  ink: { bg: T.ink, fg: '#fff', bd: T.ink },
};

type BtnSizing = { h: number; px: number; fs: number; r: number };
const BTN_SIZES: Record<BtnSize, BtnSizing> = {
  sm: { h: 32, px: 12, fs: 13, r: 10 },
  md: { h: 42, px: 16, fs: 14.5, r: 12 },
  lg: { h: 52, px: 22, fs: 16, r: 14 },
};

export function Btn({
  kind = 'primary',
  size = 'md',
  children,
  icon,
  iconAfter,
  onClick,
  style,
  disabled,
}: BtnProps): JSX.Element {
  const kinds = BTN_KINDS[kind];
  const sizes = BTN_SIZES[size];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: sizes.h,
        padding: `0 ${sizes.px}px`,
        fontSize: sizes.fs,
        fontWeight: 650,
        letterSpacing: -0.1,
        color: kinds.fg,
        background: kinds.bg,
        border: `1px solid ${kinds.bd}`,
        borderRadius: sizes.r,
        fontFamily: T.font,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={sizes.fs + 2} />}
      {children}
      {iconAfter && <Icon name={iconAfter} size={sizes.fs + 2} />}
    </button>
  );
}

// ─────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────

export type CardProps = {
  children?: ComponentChildren;
  style?: JSX.CSSProperties;
  onClick?: JSX.MouseEventHandler<HTMLDivElement>;
  pad?: number | string;
};

export function Card({
  children,
  style,
  onClick,
  pad = 14,
}: CardProps): JSX.Element {
  return (
    <div
      onClick={onClick}
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.r3,
        padding: pad,
        boxShadow: T.shadow1,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Row
// ─────────────────────────────────────────────────────────

export type RowProps = {
  children?: ComponentChildren;
  style?: JSX.CSSProperties;
  onClick?: JSX.MouseEventHandler<HTMLDivElement>;
  pad?: number | string;
};

export function Row({
  children,
  style,
  onClick,
  pad = '14px 16px',
}: RowProps): JSX.Element {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: pad,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Avatar
// ─────────────────────────────────────────────────────────

export type AvatarKind = 'ai' | 'you' | 'sys' | 'warn' | 'danger';

export type AvatarProps = {
  name?: string;
  size?: number;
  kind?: AvatarKind;
  icon?: IconName;
};

const AVATAR_PALETTE: Record<AvatarKind, { bg: string; fg: string }> = {
  ai: { bg: T.brand, fg: '#fff' },
  you: { bg: T.ink, fg: '#fff' },
  sys: { bg: T.bgAlt, fg: T.ink2 },
  warn: { bg: T.safetySoft, fg: T.safety },
  danger: { bg: T.dangerSoft, fg: T.danger },
};

export function Avatar({
  name = 'AI',
  size = 28,
  kind = 'ai',
  icon,
}: AvatarProps): JSX.Element {
  const palette = AVATAR_PALETTE[kind];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        background: palette.bg,
        color: palette.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: size * 0.42,
        flexShrink: 0,
      }}
    >
      {icon ? <Icon name={icon} size={size * 0.55} /> : name}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// SectionLabel
// ─────────────────────────────────────────────────────────

export type SectionLabelProps = {
  children?: ComponentChildren;
  style?: JSX.CSSProperties;
};

export function SectionLabel({
  children,
  style,
}: SectionLabelProps): JSX.Element {
  return (
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: T.ink3,
        padding: '12px 20px 6px',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// IconButton (helper — not in source, but mentioned as a likely export)
// ─────────────────────────────────────────────────────────

export type IconButtonProps = {
  icon: IconName;
  size?: number;
  color?: string;
  background?: string;
  onClick?: JSX.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  ariaLabel?: string;
  style?: JSX.CSSProperties;
};

export function IconButton({
  icon,
  size = 36,
  color = T.ink,
  background = 'transparent',
  onClick,
  disabled,
  ariaLabel,
  style,
}: IconButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? icon}
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background,
        color,
        border: 'none',
        borderRadius: size / 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        padding: 0,
        ...style,
      }}
    >
      <Icon name={icon} size={Math.round(size * 0.55)} color={color} />
    </button>
  );
}
